import { supabase } from '../supabaseClient';
import { getCachedUserAndProfile } from '../utils/userCache';

/**
 * 從 changes 物件提取資源所屬的 company_id / department_id
 * 優先取 after（create/update），其次取 before（delete）
 */
const extractResourceIds = (changes) => {
  const source = changes?.after || changes?.before || {};
  return {
    companyId: source.company_id || null,
    departmentId: source.department_id || null,
  };
};

/**
 * 記錄操作日誌
 *
 * company_id / department_id 記錄的是「資源所屬」的公司/部門（用於篩選），
 * 操作者身份（role, company_id, department_id）自動存入 metadata。
 *
 * 資源 company_id/department_id 取得順序：
 *   1. overrideCompanyId / overrideDepartmentId（呼叫端明確指定）
 *   2. changes.after / changes.before 中的 company_id / department_id
 *   3. null（profile 等無公司/部門概念的資源）
 *
 * @param {string} action - 操作類型
 * @param {string} resourceType - 資源類型
 * @param {string|number} resourceId - 資源 ID
 * @param {string} resourceName - 資源名稱（選填）
 * @param {object} changes - 變更內容 { before: {...}, after: {...} }（選填）
 * @param {object} metadata - 額外資訊（選填）
 * @param {string|null} overrideDepartmentId - 資源所屬部門 ID（選填）
 * @param {string|null} overrideCompanyId - 資源所屬公司 ID（選填）
 * @returns {Promise<{data: any, error: any}>}
 */
export const logAudit = async (
  action,
  resourceType,
  resourceId,
  resourceName = null,
  changes = null,
  metadata = null,
  overrideDepartmentId = null,
  overrideCompanyId = null
) => {
  try {
    const { user, profile } = await getCachedUserAndProfile(supabase);

    if (!user) {
      console.warn('⚠️ 無法記錄操作：使用者未登入');
      return { data: null, error: new Error('使用者未登入') };
    }

    // 資源所屬的 company_id / department_id（用於篩選）
    const resourceIds = extractResourceIds(changes);
    const resolvedCompanyId = overrideCompanyId !== null
      ? overrideCompanyId
      : resourceIds.companyId;
    const resolvedDepartmentId = overrideDepartmentId !== null
      ? overrideDepartmentId
      : resourceIds.departmentId;

    // 操作者上下文自動注入 metadata
    const enrichedMetadata = {
      ...(metadata || {}),
      operator_role: profile?.role || null,
      operator_company_id: profile?.company_id || null,
      operator_department_id: profile?.department_id || null,
    };

    const logData = {
      user_id: user.id,
      user_name: profile?.name || user.email,
      user_email: user.email,
      company_id: resolvedCompanyId,
      department_id: resolvedDepartmentId,
      action,
      resource_type: resourceType,
      resource_id: String(resourceId),
      resource_name: resourceName,
      changes: changes || null,
      metadata: enrichedMetadata,
    };

    const { data, error } = await supabase
      .from('audit_logs')
      .insert([logData])
      .select()
      .single();

    if (error) {
      console.error('❌ 記錄操作日誌失敗:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('❌ 記錄操作日誌異常:', error);
    return { data: null, error };
  }
};

/**
 * 批次記錄操作日誌（用於批次操作）
 * @param {Array<object>} logs - 日誌陣列，每個物件包含 { action, resourceType, resourceId, resourceName, changes, metadata }
 * @returns {Promise<{data: any, error: any}>}
 */
export const logAuditBatch = async (logs) => {
  try {
    const { user, profile } = await getCachedUserAndProfile(supabase);

    if (!user) {
      console.warn('⚠️ 無法記錄操作：使用者未登入');
      return { data: null, error: new Error('使用者未登入') };
    }

    const operatorContext = {
      operator_role: profile?.role || null,
      operator_company_id: profile?.company_id || null,
      operator_department_id: profile?.department_id || null,
    };

    const logDataArray = logs.map(log => {
      const resourceIds = extractResourceIds(log.changes);
      const enrichedMetadata = { ...(log.metadata || {}), ...operatorContext };
      return {
        user_id: user.id,
        user_name: profile?.name || user.email,
        user_email: user.email,
        company_id: resourceIds.companyId,
        department_id: resourceIds.departmentId,
        action: log.action,
        resource_type: log.resourceType,
        resource_id: String(log.resourceId),
        resource_name: log.resourceName || null,
        changes: log.changes ? JSON.stringify(log.changes) : null,
        metadata: JSON.stringify(enrichedMetadata),
      };
    });

    const { data, error } = await supabase
      .from('audit_logs')
      .insert(logDataArray)
      .select();

    if (error) {
      console.error('❌ 批次記錄操作日誌失敗:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('❌ 批次記錄操作日誌異常:', error);
    return { data: null, error };
  }
};

/**
 * 查詢操作日誌
 * @param {object} filters - 篩選條件
 *   - userId: 使用者 ID
 *   - resourceType: 資源類型
 *   - resourceId: 資源 ID
 *   - action: 操作類型
 *   - startDate: 開始日期
 *   - endDate: 結束日期
 *   - departmentId: 部門 ID
 *   - limit: 限制筆數（預設 100）
 *   - offset: 偏移量（預設 0）
 * @returns {Promise<{data: any[], error: any}>}
 */
export const getAuditLogs = async (filters = {}) => {
  try {
    let query = supabase
      .from('audit_logs')
      .select('*', { count: 'exact' })
      .order('created_at', { ascending: false });

    // 套用篩選條件
    if (filters.userId) {
      query = query.eq('user_id', filters.userId);
    }
    if (filters.resourceType) {
      query = query.eq('resource_type', filters.resourceType);
    }
    if (filters.resourceId) {
      query = query.eq('resource_id', String(filters.resourceId));
    }
    if (filters.action) {
      query = query.eq('action', filters.action);
    }
    
    // 🎯 公司和部門篩選邏輯
    if (filters.companyId) {
      // 直接用 company_id 過濾（migration 159 已經填充了 company_id）
      query = query.eq('company_id', filters.companyId);
      
      if (filters.departmentId) {
        // 如果同時指定了部門，再加上部門過濾
        query = query.eq('department_id', filters.departmentId);
      }
    } else if (filters.departmentId) {
      // 只指定部門（沒有指定公司）
      query = query.eq('department_id', filters.departmentId);
    }
    
    if (filters.startDate) {
      query = query.gte('created_at', filters.startDate);
    }
    if (filters.endDate) {
      query = query.lte('created_at', filters.endDate);
    }

    // 分頁
    const limit = filters.limit || 100;
    const offset = filters.offset || 0;
    query = query.range(offset, offset + limit - 1);

    const { data, error, count } = await query;

    if (error) {
      console.error('❌ 查詢操作日誌失敗:', error);
      return { data: null, error, count: 0 };
    }

    // 解析 JSONB 欄位
    const parsedData = data.map(log => ({
      ...log,
      changes: log.changes ? (typeof log.changes === 'string' ? JSON.parse(log.changes) : log.changes) : null,
      metadata: log.metadata ? (typeof log.metadata === 'string' ? JSON.parse(log.metadata) : log.metadata) : null
    }));

    return { data: parsedData, error: null, count };
  } catch (error) {
    console.error('❌ 查詢操作日誌異常:', error);
    return { data: null, error, count: 0 };
  }
};

/**
 * 查詢特定資源的操作歷史
 * @param {string} resourceType - 資源類型
 * @param {string|number} resourceId - 資源 ID
 * @param {number} limit - 限制筆數（預設 50）
 * @returns {Promise<{data: any[], error: any}>}
 */
export const getResourceHistory = async (resourceType, resourceId, limit = 50) => {
  return getAuditLogs({
    resourceType,
    resourceId,
    limit
  });
};

/**
 * 查詢使用者的操作歷史
 * @param {string} userId - 使用者 ID
 * @param {number} limit - 限制筆數（預設 50）
 * @returns {Promise<{data: any[], error: any}>}
 */
export const getUserHistory = async (userId, limit = 50) => {
  return getAuditLogs({
    userId,
    limit
  });
};

/**
 * 取得統計資料
 * @param {object} filters - 篩選條件（同 getAuditLogs）
 * @returns {Promise<{data: object, error: any}>}
 */
export const getAuditStats = async (filters = {}) => {
  try {
    const { data, error } = await getAuditLogs({ ...filters, limit: 10000 });
    
    if (error) {
      return { data: null, error };
    }

    // 統計資料
    const stats = {
      total: data.length,
      byAction: {},
      byResourceType: {},
      byUser: {},
      byDepartment: {}
    };

    data.forEach(log => {
      // 按操作類型統計
      stats.byAction[log.action] = (stats.byAction[log.action] || 0) + 1;
      
      // 按資源類型統計
      stats.byResourceType[log.resource_type] = (stats.byResourceType[log.resource_type] || 0) + 1;
      
      // 按使用者統計
      const userKey = log.user_name || log.user_email || 'Unknown';
      stats.byUser[userKey] = (stats.byUser[userKey] || 0) + 1;
      
      // 按部門統計
      if (log.department_id) {
        stats.byDepartment[log.department_id] = (stats.byDepartment[log.department_id] || 0) + 1;
      }
    });

    return { data: stats, error: null };
  } catch (error) {
    console.error('❌ 統計操作日誌異常:', error);
    return { data: null, error };
  }
};

/**
 * 清理舊的操作記錄（僅 Super Admin 可用）
 * @param {number} retentionDays - 保留天數（預設 365 天）
 * @returns {Promise<{data: object, error: any}>}
 */
export const cleanupOldAuditLogs = async (retentionDays = 365) => {
  try {
    const { data, error } = await supabase.rpc('cleanup_audit_logs_manual', {
      retention_days: retentionDays
    });

    if (error) {
      console.error('❌ 清理舊記錄失敗:', error);
      return { data: null, error };
    }

    // 檢查回傳的結果
    if (data && !data.success) {
      return { data: null, error: new Error(data.error) };
    }

    return { data, error: null };
  } catch (error) {
    console.error('❌ 清理舊記錄異常:', error);
    return { data: null, error };
  }
};

/**
 * 查詢今天被取消指派的 ticket IDs（DailyReport 用）
 * 用於顯示「今天完成交接」的 tickets
 * @param {string} userId - 被取消指派的使用者 ID
 * @returns {Promise<{data: string[], error: any}>}
 */
export const getTodayUnassignedTicketIds = async (userId) => {
  try {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayISO = todayStart.toISOString();

    // 查舊格式（unassign）和新格式（assignment_change）
    const [unassignRes, changeRes] = await Promise.all([
      // 舊格式：action = 'unassign'
      supabase
        .from('audit_logs')
        .select('resource_id')
        .eq('action', 'unassign')
        .eq('resource_type', 'ticket')
        .gte('created_at', todayISO)
        .filter('metadata->>target_user_id', 'eq', userId),
      // 新格式：action = 'assignment_change'，metadata.removed 裡有該用戶
      supabase
        .from('audit_logs')
        .select('resource_id, metadata')
        .eq('action', 'assignment_change')
        .eq('resource_type', 'ticket')
        .gte('created_at', todayISO),
    ]);

    if (unassignRes.error) throw unassignRes.error;

    const oldIds = (unassignRes.data || []).map(log => Number(log.resource_id));

    // 從 assignment_change logs 中篩選 removed 包含此用戶的
    const newIds = (changeRes.data || [])
      .filter(log => {
        const removed = log.metadata?.removed || [];
        return removed.some(r => r.user_id === userId);
      })
      .map(log => Number(log.resource_id));

    const ticketIds = [...new Set([...oldIds, ...newIds])];
    return { data: ticketIds, error: null };
  } catch (error) {
    console.error('Error fetching today unassigned ticket IDs:', error);
    return { data: [], error };
  }
};

/**
 * 今日被取消指派的 work_item IDs(work_item 版,對應 getTodayUnassignedTicketIds)
 * 用於 DailyReport「今天做完/今天被移除」的當日補報
 * @param {string} userId - 被取消指派的使用者 ID
 * @returns {Promise<{data: number[], error: any}>}
 */
export const getTodayUnassignedWorkItemIds = async (userId) => {
  try {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayISO = todayStart.toISOString();

    const [unassignRes, changeRes] = await Promise.all([
      // work_item 移除協作者:removeWorkItemAssignee 把被移除者寫在 changes.user_id
      // (不是 ticket 版的 metadata.target_user_id;抄查詢時沒對齊,導致規則2失效)
      supabase
        .from('audit_logs')
        .select('resource_id')
        .eq('action', 'unassign')
        .eq('resource_type', 'work_item')
        .gte('created_at', todayISO)
        .filter('changes->>user_id', 'eq', userId),
      // assignment_change:work_item 換 Owner 不會移除任何人(移除走 unassign),
      // 故 metadata.removed 對 work_item 恆空,此分支實質為 no-op,保留對齊 ticket 結構
      supabase
        .from('audit_logs')
        .select('resource_id, metadata')
        .eq('action', 'assignment_change')
        .eq('resource_type', 'work_item')
        .gte('created_at', todayISO),
    ]);

    if (unassignRes.error) throw unassignRes.error;

    const oldIds = (unassignRes.data || []).map(log => Number(log.resource_id));
    const newIds = (changeRes.data || [])
      .filter(log => (log.metadata?.removed || []).some(r => r.user_id === userId))
      .map(log => Number(log.resource_id));

    return { data: [...new Set([...oldIds, ...newIds])], error: null };
  } catch (error) {
    console.error('Error fetching today unassigned work item IDs:', error);
    return { data: [], error };
  }
};
